import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Package, 
  Bell, 
  Settings, 
  LogOut,
  Home,
  TrendingUp,
  BarChart3,
  FileText,
  Eye,
  EyeOff,
  UserPlus,
  Mail,
  Key,
  Shield,
  Menu,
  X,
  Trash2,
  ChevronDown
} from 'lucide-react';
import { signOut, createUserWithEmailAndPassword } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebase';
import { doc, getDoc, setDoc, updateDoc, deleteDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { useUserRole } from '../hooks/useUserRole';
import { getCustomers, getProductRequests, ProductRequest } from '../services/customerService';
import { getProducts } from '../services/productService';
import { getQuoteRequests, QuoteRequest } from '../services/quoteService';
import { logger } from '../utils/logger';
import ProductManagement from '../components/ProductManagement';
import CustomerManagement from '../components/CustomerManagement';
import RequestManagement from '../components/RequestManagement';
import QuoteManagement from '../components/QuoteManagement';

const AdminDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenus, setOpenMenus] = useState<string[]>(['quotes']);
  const [sidebarCounts, setSidebarCounts] = useState({
    activeQuotes: 0,
    activeDevices: 0,
    approvedQuotes: 0,
    approvedDevices: 0,
  });
  const navigate = useNavigate();
  const { user } = useUserRole();
  const [userData, setUserData] = useState<{ firstName: string; lastName: string; email: string } | null>(null);
  const [stats, setStats] = useState([
    {
      name: 'Total Customers',
      value: '0',
      change: '',
      changeType: 'neutral' as 'positive' | 'negative' | 'neutral',
      icon: Users
    },
    {
      name: 'Active Products',
      value: '0',
      change: '',
      changeType: 'neutral' as 'positive' | 'negative' | 'neutral',
      icon: Package
    },
    {
      name: 'Pending Requests',
      value: '0',
      change: '',
      changeType: 'neutral' as 'positive' | 'negative' | 'neutral',
      icon: Bell
    },
    {
      name: 'Total Requests',
      value: '0',
      change: '',
      changeType: 'neutral' as 'positive' | 'negative' | 'neutral',
      icon: TrendingUp
    }
  ]);
  const [recentRequests, setRecentRequests] = useState<ProductRequest[]>([]);
  const [recentQuoteRequests, setRecentQuoteRequests] = useState<QuoteRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // User creation state
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    userType: 'customer' as 'customer' | 'admin'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isCreatingUser, setIsCreatingUser] = useState(false);
  const [userCreationMessage, setUserCreationMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [allUsers, setAllUsers] = useState<Array<{
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    userType: string;
    createdAt: Date;
  }>>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<{ userId: string; email: string } | null>(null);
  const [changingRoleId, setChangingRoleId] = useState<string | null>(null);
  const [roleConfirm, setRoleConfirm] = useState<{
    userId: string;
    email: string;
    nextRole: 'admin' | 'customer';
  } | null>(null);
  const [resettingEmail, setResettingEmail] = useState<string | null>(null);
  const [passwordConfirm, setPasswordConfirm] = useState<{ userId: string; email: string } | null>(null);
  const [newUserPassword, setNewUserPassword] = useState('');
  const [showNewUserPassword, setShowNewUserPassword] = useState(false);
  const canManagePasswords = (user?.email || '').toLowerCase() === 'carlos.2001@hotmail.it';

  useEffect(() => {
    const loadUserData = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData({
              firstName: data.firstName || '',
              lastName: data.lastName || '',
              email: data.email || user.email || ''
            });
          }
        } catch (error) {
          logger.error('Error loading user data:', error);
        }
      }
    };

    loadUserData();
  }, [user]);

  // Load all users when Settings tab is active
  useEffect(() => {
    if (activeTab === 'settings') {
      loadAllUsers();
    }
  }, [activeTab]);

  const loadAllUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const usersCollection = collection(db, 'users');
      const usersSnapshot = await getDocs(usersCollection);
      
      const usersList = usersSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          email: data.email || '',
          firstName: data.firstName || '',
          lastName: data.lastName || '',
          userType: data.userType || 'customer',
          createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : (data.createdAt || new Date())
        };
      });

      setAllUsers(usersList);
    } catch (error) {
      logger.error('Error loading all users:', error);
      setUserCreationMessage({ type: 'error', text: 'Failed to load users list.' });
    } finally {
      setIsLoadingUsers(false);
    }
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setIsLoading(true);
        const [customers, products, requests, quoteRequests] = await Promise.all([
          getCustomers(),
          getProducts(),
          getProductRequests(),
          getQuoteRequests()
        ]);

        // Calculate stats
        const pendingRequests = requests.filter(r => r.status === 'pending');
        const pendingQuoteRequests = quoteRequests.filter(r => r.status === 'pending');
        const activeProducts = products.length;

        setStats([
          {
            name: 'Total Customers',
            value: customers.length.toLocaleString(),
            change: '',
            changeType: 'neutral',
            icon: Users
          },
          {
            name: 'Active Products',
            value: activeProducts.toLocaleString(),
            change: '',
            changeType: 'neutral',
            icon: Package
          },
          {
            name: 'Pending Requests',
            value: (pendingRequests.length + pendingQuoteRequests.length).toLocaleString(),
            change: '',
            changeType: 'neutral',
            icon: Bell
          },
          {
            name: 'Total Requests',
            value: (requests.length + quoteRequests.length).toLocaleString(),
            change: '',
            changeType: 'neutral',
            icon: TrendingUp
          }
        ]);

        // Get recent requests (last 5)
        const sortedRequests = requests
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
          .slice(0, 5);
        setRecentRequests(sortedRequests);

        // Get recent quote requests (last 5)
        const sortedQuoteRequests = quoteRequests
          .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
          .slice(0, 5);
        setRecentQuoteRequests(sortedQuoteRequests);
      } catch (error) {
        logger.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (activeTab === 'overview') {
      loadDashboardData();
    }
  }, [activeTab]);

  useEffect(() => {
    const loadSidebarCounts = async () => {
      try {
        const [requests, quoteRequests] = await Promise.all([
          getProductRequests(),
          getQuoteRequests(),
        ]);
        setSidebarCounts({
          activeQuotes: quoteRequests.filter((r) => r.status === 'pending' || r.status === 'in-progress').length,
          activeDevices: requests.filter((r) => r.status === 'pending' || r.status === 'in-progress').length,
          approvedQuotes: quoteRequests.filter((r) => r.status === 'completed').length,
          approvedDevices: requests.filter((r) => r.status === 'completed').length,
        });
      } catch (error) {
        logger.error('Error loading sidebar counts:', error);
      }
    };
    loadSidebarCounts();
  }, [activeTab]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const displayName = userData 
    ? `${userData.firstName}${userData.lastName ? ' ' + userData.lastName : ''}`.trim() || userData.email
    : user?.email || 'Admin';
  const displayEmail = userData?.email || user?.email || '';
  const displayInitials = displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('') || 'A';

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'in-progress':
        return 'bg-gray-100 text-gray-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const quoteTabs = ['quotes-active', 'quotes-approved'];
  const deviceTabs = ['devices-active', 'devices-approved'];
  const tabTitles: Record<string, string> = {
    overview: 'Overview',
    products: 'Products',
    customers: 'Customers',
    'quotes-active': 'Quote requests',
    'devices-active': 'Device requests',
    'quotes-approved': 'Approved quotes',
    'devices-approved': 'Approved devices',
    reports: 'Reports',
    settings: 'Settings',
  };

  const selectTab = (id: string, parentId?: string) => {
    setActiveTab(id);
    if (parentId) {
      setOpenMenus((prev) => (prev.includes(parentId) ? prev : [...prev, parentId]));
    }
    setMobileMenuOpen(false);
  };

  const toggleMenu = (id: string) => {
    setOpenMenus((prev) =>
      prev.includes(id) ? prev.filter((menu) => menu !== id) : [...prev, id]
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'products':
        return <ProductManagement />;
      case 'customers':
        return <CustomerManagement />;
      case 'quotes-active':
      case 'quotes':
        return <QuoteManagement view="active" />;
      case 'quotes-approved':
        return <QuoteManagement view="approved" />;
      case 'devices-active':
      case 'requests':
        return <RequestManagement view="active" />;
      case 'devices-approved':
        return <RequestManagement view="approved" />;
      case 'reports':
        return renderReports();
      case 'settings':
        return renderSettings();
      default:
        return renderOverview();
    }
  };

  const renderOverview = () => (
    <div className="space-y-4 sm:space-y-6 md:space-y-8">
      {isLoading ? (
        <div className="text-center py-8 sm:py-12">
          <p className="text-sm sm:text-base text-gray-600">Loading dashboard data...</p>
        </div>
      ) : (
        <>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white rounded-lg shadow-sm sm:shadow p-4 sm:p-5 md:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 mb-1">{stat.name}</p>
                <p className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900">{stat.value}</p>
                    {stat.change && (
                <p className={`text-xs sm:text-sm mt-1 ${
                        stat.changeType === 'positive' ? 'text-green-600' : 
                        stat.changeType === 'negative' ? 'text-red-600' : 
                        'text-gray-600'
                }`}>
                        {stat.change}
                </p>
                    )}
              </div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 sm:ml-2">
                <stat.icon className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Requests */}
      <div className="bg-white rounded-lg shadow-sm sm:shadow">
        <div className="p-4 sm:p-5 md:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Recent Customer Requests</h2>
        </div>
        <div className="p-4 sm:p-5 md:p-6">
              {recentRequests.length === 0 && recentQuoteRequests.length === 0 ? (
                <p className="text-sm sm:text-base text-gray-600 text-center py-6 sm:py-8">No recent requests found.</p>
              ) : (
          <div className="space-y-3 sm:space-y-4">
            {/* Show Quote Requests first */}
            {recentQuoteRequests.map((request) => (
              <div key={`quote-${request.id}`} className="bg-gray-50 rounded-lg p-4 sm:p-5 border border-gray-200">
                <div className="flex flex-col gap-3">
                  {/* Top row: Business name and status */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1.5 break-words">{request.businessName || 'Quote Request'}</h3>
                      <p className="text-sm sm:text-base text-gray-600">{request.systemType || 'General Inquiry'}</p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full flex-shrink-0 ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                  </div>
                  
                  {/* Middle row: Business type */}
                  <div>
                    <p className="text-sm sm:text-base text-gray-700">{request.businessType || 'Quote Request'}</p>
                  </div>
                  
                  {/* Bottom row: Date and button */}
                  <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-200">
                    <p className="text-sm text-gray-500">{request.createdAt.toLocaleDateString()}</p>
                    <button 
                      onClick={() => selectTab('quotes-active')}
                      className="text-sm sm:text-base text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap px-2 py-1"
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              </div>
            ))}
            {/* Then show Product Requests */}
            {recentRequests.map((request) => (
              <div key={request.id} className="bg-gray-50 rounded-lg p-4 sm:p-5 border border-gray-200">
                <div className="flex flex-col gap-3">
                  {/* Top row: Customer name and status */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1.5 break-words">{request.customerName || request.customerEmail}</h3>
                      <p className="text-sm sm:text-base text-gray-600">{request.productName}</p>
                    </div>
                    <span className={`inline-flex items-center px-3 py-1.5 text-xs sm:text-sm font-medium rounded-full flex-shrink-0 ${getStatusColor(request.status)}`}>
                      {request.status}
                    </span>
                  </div>
                  
 
                  {request.description && (
                    <div>
                      <p className="text-sm sm:text-base text-gray-700 line-clamp-2">{request.description}</p>
                    </div>
                  )}
                  

                  <div className="flex items-center justify-between gap-3 pt-2 border-t border-gray-200">
                    <p className="text-sm text-gray-500">{request.createdAt.toLocaleDateString()}</p>
                    <button 
                      onClick={() => selectTab('devices-active')}
                      className="text-sm sm:text-base text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap px-2 py-1"
                    >
                      View Details →
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
              )}
        </div>
      </div>
        </>
      )}
    </div>
  );

  const renderReports = () => (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Reports & Analytics</h2>
      <div className="bg-white rounded-lg shadow-sm sm:shadow p-4 sm:p-6">
        <p className="text-sm sm:text-base text-gray-600">Reporting features coming soon</p>
      </div>
    </div>
  );

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user || !user.email) {
      setUserCreationMessage({ type: 'error', text: 'Admin user not found. Please log in again.' });
      return;
    }

    if (!newUser.email || !newUser.password) {
      setUserCreationMessage({ type: 'error', text: 'Please fill in all required fields.' });
      return;
    }

    if (newUser.password.length < 6) {
      setUserCreationMessage({ type: 'error', text: 'Password must be at least 6 characters long.' });
      return;
    }

    setIsCreatingUser(true);
    setUserCreationMessage(null);

    try {
      // Store current admin info
      const adminEmail = user.email;
      
      // Create the new user (this will temporarily sign them in)
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        newUser.email,
        newUser.password
      );

      // Save user data to Firestore with the specified role
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        firstName: '',
        lastName: '',
        email: newUser.email,
        createdAt: new Date(),
        userType: newUser.userType,
        provider: 'email',
        createdBy: adminEmail
      });

      // Sign out the newly created user immediately
      await signOut(auth);
      
      // Clear form before navigating
      const createdUserType = newUser.userType;
      const createdEmail = newUser.email;
      
      setNewUser({
        email: '',
        password: '',
        userType: 'customer'
      });
      
      setUserCreationMessage({ 
        type: 'success', 
        text: `Successfully created ${createdUserType} account for ${createdEmail}! The new user account is ready.` 
      });

      // Reload users list
      await loadAllUsers();

      

    } catch (error: any) {
      logger.error('Error creating user:', error);
      
      if (error.code === 'auth/email-already-in-use') {
        setUserCreationMessage({ type: 'error', text: 'This email is already registered.' });
      } else if (error.code === 'auth/invalid-email') {
        setUserCreationMessage({ type: 'error', text: 'Invalid email address.' });
      } else if (error.code === 'auth/weak-password') {
        setUserCreationMessage({ type: 'error', text: 'Password is too weak. Please use a stronger password.' });
      } else {
        setUserCreationMessage({ type: 'error', text: `Failed to create user: ${error.message}` });
      }
    } finally {
      setIsCreatingUser(false);
    }
  };

  const handleRoleChangeClick = (
    listUser: { id: string; email: string; userType: string },
    nextRole: 'admin' | 'customer'
  ) => {
    if (listUser.id === user?.uid) {
      setUserCreationMessage({ type: 'error', text: 'You cannot change your own role.' });
      return;
    }
    if (nextRole === 'customer') {
      const adminCount = allUsers.filter(u => u.userType === 'admin').length;
      if (adminCount <= 1) {
        setUserCreationMessage({ type: 'error', text: 'You cannot demote the last remaining admin.' });
        return;
      }
    }
    setRoleConfirm({ userId: listUser.id, email: listUser.email, nextRole });
  };

  const handleSetPasswordClick = (listUser: { id: string; email: string }) => {
    if (!canManagePasswords) return;
    setPasswordConfirm({ userId: listUser.id, email: listUser.email });
    setNewUserPassword('');
    setShowNewUserPassword(false);
    setUserCreationMessage(null);
  };

  const handleConfirmSetPassword = async () => {
    if (!canManagePasswords || !passwordConfirm) return;
    if (newUserPassword.length < 6) {
      setUserCreationMessage({ type: 'error', text: 'Password must be at least 6 characters.' });
      return;
    }

    setResettingEmail(passwordConfirm.email);
    setUserCreationMessage(null);
    try {
      const setUserPassword = httpsCallable(functions, 'setUserPassword');
      await setUserPassword({ uid: passwordConfirm.userId, password: newUserPassword });
      setUserCreationMessage({
        type: 'success',
        text: `Password updated for ${passwordConfirm.email}. They can sign in with the new password now.`,
      });
      setPasswordConfirm(null);
      setNewUserPassword('');
    } catch (error: any) {
      logger.error('Error setting user password:', error);
      setUserCreationMessage({
        type: 'error',
        text: error?.message || 'Could not set the password. Try again.',
      });
    } finally {
      setResettingEmail(null);
    }
  };

  const handleConfirmRoleChange = async () => {
    if (!roleConfirm) return;
    const { userId, email, nextRole } = roleConfirm;
    setChangingRoleId(userId);
    setRoleConfirm(null);
    setUserCreationMessage(null);
    try {
      await updateDoc(doc(db, 'users', userId), {
        userType: nextRole,
        role: nextRole,
      });
      setUserCreationMessage({
        type: 'success',
        text: nextRole === 'admin'
          ? `${email} is now an admin. They will see the admin dashboard the next time they sign in.`
          : `${email} is now a customer.`
      });
      await loadAllUsers();
    } catch (error: any) {
      logger.error('Error changing user role:', error);
      setUserCreationMessage({ type: 'error', text: error.message || 'Failed to update user role.' });
    } finally {
      setChangingRoleId(null);
    }
  };

  const handleRemoveUserClick = (userId: string, userEmail: string) => {
    if (userId === user?.uid) {
      setUserCreationMessage({ type: 'error', text: 'You cannot remove your own account.' });
      return;
    }
    setRemoveConfirm({ userId, email: userEmail });
  };

  const handleConfirmRemoveUser = async () => {
    if (!removeConfirm) return;
    const { userId, email } = removeConfirm;
    setRemovingUserId(userId);
    setRemoveConfirm(null);
    setUserCreationMessage(null);
    try {
      await deleteDoc(doc(db, 'users', userId));
      setUserCreationMessage({ type: 'success', text: `User ${email} has been removed.` });
      await loadAllUsers();
    } catch (error: any) {
      logger.error('Error removing user:', error);
      setUserCreationMessage({ type: 'error', text: error.message || 'Failed to remove user.' });
    } finally {
      setRemovingUserId(null);
    }
  };

  const renderSettings = () => (
    <div className="space-y-4 sm:space-y-6">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900">System Settings</h2>
      
      {/* User Creation Section - Only for admins */}
      <div className="bg-white rounded-lg shadow-sm sm:shadow p-4 sm:p-5 md:p-6">
        <div className="flex items-center mb-4">
          <UserPlus className="h-6 w-6 text-gray-700 mr-2" />
          <h3 className="text-xl font-semibold text-gray-900">Create User Account</h3>
        </div>
        <p className="text-gray-600 mb-6">Create new customer or admin accounts. You will remain logged in.</p>
        
        {userCreationMessage && (
          <div className={`mb-4 p-4 rounded-lg ${
            userCreationMessage.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {userCreationMessage.text}
          </div>
        )}

        <form onSubmit={handleCreateUser} className="space-y-4">
          {/* User Type */}
          <div>
            <label htmlFor="userType" className="block text-sm font-medium text-gray-700 mb-2">
              Account Type <span className="text-red-500">*</span>
            </label>
            <select
              id="userType"
              value={newUser.userType}
              onChange={(e) => setNewUser({ ...newUser, userType: e.target.value as 'customer' | 'admin' })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
              disabled={isCreatingUser}
            >
              <option value="customer">Customer</option>
              <option value="admin">Admin</option>
            </select>
          </div>

         
          <div>
            <label htmlFor="newUserEmail" className="block text-sm font-medium text-gray-700 mb-2">
              <Mail className="inline h-4 w-4 mr-1" />
              Email Address <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="newUserEmail"
              value={newUser.email}
              onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="user@example.com"
              required
              disabled={isCreatingUser}
            />
          </div>

          {/* Password */}
          <div>
            <label htmlFor="newUserPassword" className="block text-sm font-medium text-gray-700 mb-2">
              <Key className="inline h-4 w-4 mr-1" />
              Password <span className="text-red-500">*</span>
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                id="newUserPassword"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Minimum 6 characters"
                required
                minLength={6}
                disabled={isCreatingUser}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                disabled={isCreatingUser}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            <p className="mt-1 text-xs text-gray-500">Minimum 6 characters required</p>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isCreatingUser}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
          >
            {isCreatingUser ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                Creating User...
              </>
            ) : (
              <>
                <UserPlus className="h-5 w-5" />
                Create {newUser.userType === 'admin' ? 'Admin' : 'Customer'} Account
              </>
            )}
          </button>
        </form>
      </div>

      {/* Users List - Admins and Customers */}
      <div className="bg-white rounded-lg shadow-sm sm:shadow p-4 sm:p-5 md:p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <Users className="h-6 w-6 text-gray-700 mr-2" />
            <h3 className="text-xl font-semibold text-gray-900">Registered Users</h3>
          </div>
          <button
            onClick={loadAllUsers}
            disabled={isLoadingUsers}
            className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoadingUsers ? 'Loading...' : 'Refresh'}
          </button>
        </div>

        {isLoadingUsers ? (
          <div className="text-center py-6 sm:py-8">
            <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-sm sm:text-base text-gray-600">Loading users...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Admins Column */}
            <div>
              <div className="flex items-center mb-4">
                <Shield className="h-5 w-5 text-blue-600 mr-2" />
                <h4 className="text-lg font-semibold text-gray-900">Admins</h4>
                <span className="ml-2 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                  {allUsers.filter(u => u.userType === 'admin').length}
                </span>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {allUsers.filter(u => u.userType === 'admin').length === 0 ? (
                  <p className="text-gray-500 text-xs sm:text-sm py-3 sm:py-4 text-center">No admins found</p>
                ) : (
                  allUsers
                    .filter(u => u.userType === 'admin')
                    .sort((a, b) => a.email.localeCompare(b.email))
                    .map((listUser) => (
                      <div
                        key={listUser.id}
                        className="p-2.5 sm:p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-xs sm:text-sm truncate">
                              {listUser.firstName || listUser.lastName
                                ? `${listUser.firstName} ${listUser.lastName}`.trim()
                                : 'No name'}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">{listUser.email}</p>
                            <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                              Joined: {listUser.createdAt.toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {canManagePasswords && (
                              <button
                                type="button"
                                onClick={() => handleSetPasswordClick(listUser)}
                                disabled={resettingEmail === listUser.email}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] sm:text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Set a new password"
                              >
                                {resettingEmail === listUser.email ? (
                                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-200 border-t-gray-700" />
                                ) : (
                                  <Key className="h-3.5 w-3.5" />
                                )}
                                <span className="hidden sm:inline">Set password</span>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRoleChangeClick(listUser, 'customer')}
                              disabled={listUser.id === user?.uid || changingRoleId === listUser.id || removingUserId === listUser.id}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] sm:text-xs font-medium text-green-700 bg-green-50 hover:bg-green-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title={listUser.id === user?.uid ? 'Cannot change your own role' : 'Make customer'}
                            >
                              {changingRoleId === listUser.id ? (
                                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-green-200 border-t-green-700" />
                              ) : (
                                <Users className="h-3.5 w-3.5" />
                              )}
                              <span className="hidden sm:inline">Make customer</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveUserClick(listUser.id, listUser.email)}
                              disabled={listUser.id === user?.uid || removingUserId === listUser.id}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title={listUser.id === user?.uid ? 'Cannot remove yourself' : 'Remove user'}
                            >
                              {removingUserId === listUser.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-red-600" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>

          
            <div>
              <div className="flex items-center mb-4">
                <Users className="h-5 w-5 text-green-600 mr-2" />
                <h4 className="text-lg font-semibold text-gray-900">Customers</h4>
                <span className="ml-2 px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">
                  {allUsers.filter(u => u.userType === 'customer' || !u.userType).length}
                </span>
              </div>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {allUsers.filter(u => u.userType === 'customer' || !u.userType).length === 0 ? (
                  <p className="text-gray-500 text-xs sm:text-sm py-3 sm:py-4 text-center">No customers found</p>
                ) : (
                  allUsers
                    .filter(u => u.userType === 'customer' || !u.userType)
                    .sort((a, b) => a.email.localeCompare(b.email))
                    .map((listUser) => (
                      <div
                        key={listUser.id}
                        className="p-2.5 sm:p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-xs sm:text-sm truncate">
                              {listUser.firstName || listUser.lastName
                                ? `${listUser.firstName} ${listUser.lastName}`.trim()
                                : 'No name'}
                            </p>
                            <p className="text-xs sm:text-sm text-gray-600 mt-1 truncate">{listUser.email}</p>
                            <p className="text-[10px] sm:text-xs text-gray-500 mt-1">
                              Joined: {listUser.createdAt.toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            {canManagePasswords && (
                              <button
                                type="button"
                                onClick={() => handleSetPasswordClick(listUser)}
                                disabled={resettingEmail === listUser.email}
                                className="inline-flex items-center gap-1 px-2 py-1 text-[10px] sm:text-xs font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Set a new password"
                              >
                                {resettingEmail === listUser.email ? (
                                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-gray-200 border-t-gray-700" />
                                ) : (
                                  <Key className="h-3.5 w-3.5" />
                                )}
                                <span className="hidden sm:inline">Set password</span>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRoleChangeClick(listUser, 'admin')}
                              disabled={listUser.id === user?.uid || changingRoleId === listUser.id || removingUserId === listUser.id}
                              className="inline-flex items-center gap-1 px-2 py-1 text-[10px] sm:text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title={listUser.id === user?.uid ? 'Cannot change your own role' : 'Make admin'}
                            >
                              {changingRoleId === listUser.id ? (
                                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-blue-200 border-t-blue-700" />
                              ) : (
                                <Shield className="h-3.5 w-3.5" />
                              )}
                              <span className="hidden sm:inline">Make admin</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveUserClick(listUser.id, listUser.email)}
                              disabled={listUser.id === user?.uid || removingUserId === listUser.id}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                              title={listUser.id === user?.uid ? 'Cannot remove yourself' : 'Remove user'}
                            >
                              {removingUserId === listUser.id ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-300 border-t-red-600" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderSidebar = () => (
    <div className="flex h-full flex-col">
      <div className="px-5 pt-6 pb-5">
        <button type="button" onClick={handleGoHome} className="flex items-center">
          <img src="/logo.png?v=1" alt="Simpl Processing" className="h-8 w-auto" />
        </button>
      </div>

      <div className="px-5 pb-5 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-gray-900 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
            {displayInitials}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
            <p className="text-xs text-gray-500 truncate">{displayEmail}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs">
          <button
            type="button"
            onClick={() => {
              setActiveTab('settings');
              setMobileMenuOpen(false);
            }}
            className="text-gray-500 hover:text-gray-900"
          >
            Profile
          </button>
          <span className="text-gray-300">|</span>
          <button
            type="button"
            onClick={handleLogout}
            className="text-gray-500 hover:text-red-600"
          >
            Logout
          </button>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-3" aria-label="Admin">
        {([
          { id: 'overview', name: 'Overview', icon: BarChart3 },
          { id: 'products', name: 'Products', icon: Package },
          { id: 'customers', name: 'Customers', icon: Users },
        ] as const).map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectTab(tab.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 mb-0.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <tab.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-gray-900' : 'text-gray-400'}`} />
              <span>{tab.name}</span>
            </button>
          );
        })}

        {([
          {
            id: 'quotes',
            name: 'Quotes',
            icon: FileText,
            defaultChild: 'quotes-active',
            childIds: quoteTabs,
            children: [
              { id: 'quotes-active', name: 'Requests', badge: sidebarCounts.activeQuotes },
              { id: 'quotes-approved', name: 'Approved', badge: sidebarCounts.approvedQuotes },
            ],
          },
          {
            id: 'devices',
            name: 'Devices',
            icon: Bell,
            defaultChild: 'devices-active',
            childIds: deviceTabs,
            children: [
              { id: 'devices-active', name: 'Requests', badge: sidebarCounts.activeDevices },
              { id: 'devices-approved', name: 'Approved', badge: sidebarCounts.approvedDevices },
            ],
          },
        ]).map((group) => {
          const isGroupActive = group.childIds.includes(activeTab);
          const isOpen = openMenus.includes(group.id);
          return (
            <div key={group.id} className="mt-1 mb-1">
              <button
                type="button"
                onClick={() => toggleMenu(group.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isGroupActive
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <group.icon className={`h-4 w-4 flex-shrink-0 ${isGroupActive ? 'text-gray-900' : 'text-gray-400'}`} />
                <span className="flex-1 text-left">{group.name}</span>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
              {isOpen && (
                <div className="ml-4 mt-0.5 pl-3 border-l border-gray-200">
                  {group.children.map((child) => {
                    const isActive = activeTab === child.id;
                    return (
                      <button
                        key={child.id}
                        type="button"
                        onClick={() => selectTab(child.id, group.id)}
                        className={`w-full flex items-center justify-between px-3 py-2 mb-0.5 rounded-lg text-sm transition-colors ${
                          isActive
                            ? 'bg-gray-900 text-white'
                            : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                        }`}
                      >
                        <span>{child.name}</span>
                        {child.badge > 0 && (
                          <span className={`text-[10px] rounded-full h-5 min-w-[20px] px-1 flex items-center justify-center ${
                            isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {child.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {([
          { id: 'reports', name: 'Reports', icon: TrendingUp },
          { id: 'settings', name: 'Settings', icon: Settings },
        ] as const).map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => selectTab(tab.id)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 mb-0.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'bg-gray-100 text-gray-900'
                  : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <tab.icon className={`h-4 w-4 flex-shrink-0 ${isActive ? 'text-gray-900' : 'text-gray-400'}`} />
              <span>{tab.name}</span>
            </button>
          );
        })}
      </nav>

      <div className="px-5 py-4 border-t border-gray-100">
        <button
          type="button"
          onClick={handleGoHome}
          className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-900"
        >
          <Home className="h-3.5 w-3.5" />
          Back to website
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f4f5f7] flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col bg-white border-r border-gray-200 sticky top-0 h-screen">
        {renderSidebar()}
      </aside>

      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileMenuOpen(false)} aria-hidden />
          <aside className="relative z-50 h-full w-72 max-w-[85vw] bg-white shadow-xl">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(false)}
              className="absolute top-4 right-4 p-1.5 text-gray-400 hover:text-gray-700 rounded-lg"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
            {renderSidebar()}
          </aside>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-3 sticky top-0 z-30">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden p-2 -ml-2 text-gray-600 hover:bg-gray-50 rounded-lg"
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">
              {tabTitles[activeTab] || 'Dashboard'}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 truncate">Welcome back, {displayName}</p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="ml-auto hidden sm:inline-flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </header>

        <main className="flex-1 overflow-auto px-4 sm:px-6 lg:px-8 py-5 sm:py-6">
          {renderTabContent()}
        </main>
      </div>

      {/* Change role confirmation modal */}
      {roleConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setRoleConfirm(null)}
            aria-hidden
          />
          <div
            className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl ring-1 ring-black/5"
            role="dialog"
            aria-labelledby="change-role-title"
            aria-modal="true"
          >
            <div className="flex items-start gap-4">
              <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${
                roleConfirm.nextRole === 'admin' ? 'bg-blue-100' : 'bg-green-100'
              }`}>
                {roleConfirm.nextRole === 'admin' ? (
                  <Shield className="h-6 w-6 text-blue-600" />
                ) : (
                  <Users className="h-6 w-6 text-green-600" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 id="change-role-title" className="text-lg font-semibold text-gray-900">
                  {roleConfirm.nextRole === 'admin' ? 'Make this user an admin?' : 'Make this user a customer?'}
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  {roleConfirm.nextRole === 'admin' ? (
                    <>
                      <span className="font-medium text-gray-900">{roleConfirm.email}</span> will be able to manage products, customers, quotes, and other users.
                    </>
                  ) : (
                    <>
                      <span className="font-medium text-gray-900">{roleConfirm.email}</span> will lose admin access and only see the customer dashboard.
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setRoleConfirm(null)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRoleChange}
                className={`rounded-lg px-4 py-2.5 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                  roleConfirm.nextRole === 'admin'
                    ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500'
                    : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
                }`}
              >
                {roleConfirm.nextRole === 'admin' ? 'Make admin' : 'Make customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {passwordConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => {
              setPasswordConfirm(null);
              setNewUserPassword('');
            }}
            aria-hidden
          />
          <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl ring-1 ring-black/5">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gray-100">
                <Key className="h-6 w-6 text-gray-700" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900">Set a new password</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Type the password you want for{' '}
                  <span className="font-medium text-gray-900">{passwordConfirm.email}</span>.
                  They can sign in with it right away.
                </p>
              </div>
            </div>
            <div className="mt-4 relative">
              <input
                type={showNewUserPassword ? 'text' : 'password'}
                value={newUserPassword}
                onChange={(e) => setNewUserPassword(e.target.value)}
                placeholder="Minimum 6 characters"
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
              />
              <button
                type="button"
                onClick={() => setShowNewUserPassword((open) => !open)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              >
                {showNewUserPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => {
                  setPasswordConfirm(null);
                  setNewUserPassword('');
                }}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmSetPassword}
                disabled={resettingEmail === passwordConfirm.email || newUserPassword.length < 6}
                className="rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-black disabled:opacity-50"
              >
                {resettingEmail === passwordConfirm.email ? 'Saving...' : 'Save password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remove user confirmation modal */}
      {removeConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setRemoveConfirm(null)}
            aria-hidden
          />
          <div
            className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-xl ring-1 ring-black/5"
            role="dialog"
            aria-labelledby="remove-user-title"
            aria-modal="true"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 id="remove-user-title" className="text-lg font-semibold text-gray-900">
                  Remove user?
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Remove <span className="font-medium text-gray-900">{removeConfirm.email}</span>? They will lose access to the app. This cannot be undone.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => setRemoveConfirm(null)}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmRemoveUser}
                className="rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                Remove user
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
